import {
  buildLeadershipAlertPrompt,
  accountChangeRowMatchesSelectedProduct,
  withDoctorNamePrefix,
  getSalesRepPerformanceRowsByProduct,
  getRegionDisplayName,
  isAllProductGroupSelection,
} from "./myPerformanceHelpers";
import { mapColorCodingToSeverity } from "./widgets/shared/severityMappers";
import { REGIONAL_PERFORMANCE_TEXT } from "./widgets/PerformanceByTerritory/performanceTextConstants";
import { TREATMENT_RATE_WIDGET_TEXT } from "../widgets/treatmentRateTextConstants";

/** "SOUTHEAST" -> "Southeast", "MID-ATLANTIC" -> "Mid-Atlantic". */
function toTitleCase(value) {
  return (value ?? "")
    .toString()
    .toLowerCase()
    .replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

/**
 * Regional Performance subtitle with the asker's region appended, e.g.
 * "Percent to Goal by Territory (Southeast Region)". Falls back to the plain
 * subtitle when no region is present in the payload.
 */
function buildRegionalPerformanceSubtitle(regionName) {
  const region = toTitleCase(regionName).trim();
  return region
    ? `${REGIONAL_PERFORMANCE_TEXT.subtitle} (${region} Region)`
    : REGIONAL_PERFORMANCE_TEXT.subtitle;
}

// Rounds a numeric value to (at most) 2 decimal places, preserving fractional
// values that would otherwise be lost if we snapped to a whole number.
function round2(value) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * 10) / 10 : 0;
}

function parseDecileTargetTier(value) {
  const raw = (value ?? "").toString().trim();
  if (!raw) return { tierLabel: "-", tierSubtitle: "" };
  const open = raw.indexOf("(");
  const close = raw.lastIndexOf(")");
  if (open !== -1 && close > open) {
    return {
      tierLabel: raw.slice(0, open).trim(),
      tierSubtitle: raw.slice(open + 1, close).trim(),
    };
  }
  return { tierLabel: raw, tierSubtitle: "" };
}

/** Maps `/kpi_metrics/reach_frequency` rows (PascalCase keys) to ReachAndFrequencyCard rows. */
function mapReachFrequencyApiRowsToCardRows(apiList) {
  if (!Array.isArray(apiList) || apiList.length === 0) return [];
  return apiList.map((raw, idx) => {
    const tier = (raw?.DecileTargetTier ?? "").toString().trim();
    const { tierLabel, tierSubtitle } = parseDecileTargetTier(tier);
    const reachRaw = raw?.Reach;
    const freqRaw = raw?.Frequency;
    const reachNum =
      reachRaw === null || reachRaw === undefined || reachRaw === ""
        ? null
        : Number(reachRaw);
    const freqNum =
      freqRaw === null || freqRaw === undefined || freqRaw === ""
        ? null
        : Number(freqRaw);
    const reachPct =
      reachNum === null || Number.isNaN(reachNum)
        ? null
        : reachNum > 0 && reachNum <= 1
          ? reachNum * 100
          : reachNum;
    const frequency =
      freqNum === null || Number.isNaN(freqNum) ? null : freqNum;
    return {
      id: `rf-${idx}`,
      tierLabel: tierLabel || "-",
      tierSubtitle,
      avgReachPercent: reachPct,
      reachFlag: raw?.Reach_Flag,
      avgFrequency: frequency,
      frequencyFlag: raw?.Frequency_Flag,
    };
  });
}

function formatReferralMonthLabel(monthEndDate) {
  const raw = (monthEndDate ?? "").toString().trim();
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const month = d.toLocaleString("en-US", { month: "short" });
  const year = String(d.getFullYear()).slice(-2);
  return `${month} '${year}`;
}

function normalizeFilterKey(value) {
  return (value ?? "").toString().trim().toUpperCase();
}

export function mapTyvasoDpiDosageDistribution(data) {
  const rows = Array.isArray(data)
    ? data
    : data?.tyvaso_dpi_dosage_distribution;
  if (!Array.isArray(rows)) return [];

  return rows.map((row, index) => ({
    id: row?.ACCOUNTID || `tyvaso-dpi-dose-${index}`,
    accountName: row?.ACCOUNTNAME || "",
    product: row?.PRODUCT || "",
    lessThan48: Number(row?.Dose_LessThan_48 ?? 0),
    dose48: Number(row?.Dose_48 ?? 0),
    dose64: Number(row?.Dose_64 ?? 0),
    dose80: Number(row?.Dose_80 ?? 0),
    greaterThan80: Number(row?.Dose_GreaterThan_80 ?? 0),
  }));
}

export function mapTyvasoDpiDosageNotifications(data) {
  const rows = Array.isArray(data)
    ? data
    : data?.tyvaso_dpi_dosage_notification;
  if (!Array.isArray(rows)) return [];

  return rows
    .map((row, index) => {
      const accountName = (row?.AccountName ?? row?.accountName ?? "")
        .toString()
        .trim();
      const patientsReady = Number(
        row?.Patient_Count ?? row?.patientsReady ?? 0,
      );
      return {
        id: `tyvaso-dpi-alert-${index}`,
        accountName,
        patientsReady: Number.isFinite(patientsReady) ? patientsReady : 0,
      };
    })
    .filter((row) => row.accountName)
    .sort((a, b) => {
      if (b.patientsReady !== a.patientsReady) {
        return b.patientsReady - a.patientsReady;
      }
      return a.accountName.localeCompare(b.accountName);
    });
}

function isValidTreatmentSalesGroup(value) {
  const key = normalizeFilterKey(value);
  return Boolean(key) && key !== "0";
}

function treatmentRateRowMatchesProduct(
  row,
  selectedProduct,
  { leadershipSelection = null, isLeadership = false } = {},
) {
  const key = normalizeFilterKey(selectedProduct);
  const rowSalesGroup = normalizeFilterKey(
    row?.Sales_Group ?? row?.SalesGroup ?? row?.sales_group,
  );
  const rowProduct = normalizeFilterKey(row?.ProductGroup ?? row?.productgroup);

  if (!isValidTreatmentSalesGroup(rowSalesGroup)) return false;

  if (isLeadership && key === "ALL") {
    return rowSalesGroup === "ALL";
  }

  if (!key) return true;

  if (key === "ALL") {
    return rowProduct === "ALL";
  }

  if (leadershipSelection) {
    const productKey = normalizeFilterKey(leadershipSelection.product);
    const salesGroupKey = normalizeFilterKey(leadershipSelection.salesGroup);
    if (productKey && rowProduct !== productKey && rowProduct !== "ALL")
      return false;
    if (salesGroupKey && rowSalesGroup !== salesGroupKey) return false;
    return true;
  }

  if (!rowProduct) return false;
  if (key === "TYVASO" && rowProduct.startsWith("TYVASO")) return true;
  return rowProduct === key;
}

function aggregateTreatmentRateByMonth(rows) {
  const byMonth = new Map();
  for (const row of rows) {
    const monthKey = row.monthEndDate || row.month;
    const existing = byMonth.get(monthKey);
    if (!existing) {
      byMonth.set(monthKey, { ...row, _count: 1 });
      continue;
    }
    existing.treatmentRate += row.treatmentRate;
    existing.percentagePending += row.percentagePending;
    existing.timeToTherapy += row.timeToTherapy;
    existing._count += 1;
  }
  return Array.from(byMonth.values()).map(({ _count, ...row }) => ({
    ...row,
    treatmentRate: Number((row.treatmentRate / _count).toFixed(1)),
    percentagePending: Number((row.percentagePending / _count).toFixed(1)),
    timeToTherapy: Math.round(row.timeToTherapy / _count),
  }));
}

function buildPartialMonthNote(chartRows) {
  if (!Array.isArray(chartRows) || chartRows.length === 0) return null;
  const latest = chartRows[chartRows.length - 1];
  const end = latest?.monthEndDate ? new Date(latest.monthEndDate) : null;
  if (!end || Number.isNaN(end.getTime())) return null;
  const now = new Date();
  if (
    end.getFullYear() !== now.getFullYear() ||
    end.getMonth() !== now.getMonth()
  ) {
    return null;
  }
  return `* ${latest.month} is a partial month`;
}

/** Maps treatment rate API rows to TreatmentRateWidget chart data. */
function mapTreatmentRateApiRowsToChartRows(
  apiList,
  selectedProduct,
  { leadershipSelection = null, isLeadership = false } = {},
) {
  if (!Array.isArray(apiList) || apiList.length === 0) return [];

  let filtered = apiList.filter((row) =>
    treatmentRateRowMatchesProduct(row, selectedProduct, {
      leadershipSelection,
      isLeadership,
    }),
  );

  const mapped = filtered
    .sort((a, b) => new Date(a.MonthEndDate) - new Date(b.MonthEndDate))
    .map((raw) => ({
      month: formatReferralMonthLabel(raw?.MonthEndDate),
      monthEndDate: raw?.MonthEndDate,
      treatmentRate: Number(raw?.TreatmentRate ?? 0),
      percentagePending: Number(raw?.PotentialTreatmentRate ?? 0),
      timeToTherapy: Number(raw?.TimeToTherapy ?? 0),
    }));

  const uniqueMonths = new Set(
    mapped.map((row) => row.monthEndDate || row.month),
  );
  if (mapped.length > uniqueMonths.size) {
    return aggregateTreatmentRateByMonth(mapped);
  }
  return mapped;
}

function buildTreatmentRateWidgetProps({
  treatmentRateRows,
  effectiveSelectedProduct,
  leadershipSelection = null,
  isLeadership = false,
  isLoading = false,
  isError = false,
  onExplore,
}) {
  const chartData = mapTreatmentRateApiRowsToChartRows(
    treatmentRateRows,
    effectiveSelectedProduct,
    { leadershipSelection, isLeadership },
  );
  const productLabel = isAllProductGroupSelection(effectiveSelectedProduct)
    ? "All"
    : (effectiveSelectedProduct || "TYVASO").toString().trim();
  return {
    data: chartData,
    title: TREATMENT_RATE_WIDGET_TEXT.title,
    subtitle: `${productLabel} · ${TREATMENT_RATE_WIDGET_TEXT.subtitleSuffix}`,
    partialMonthNote: buildPartialMonthNote(chartData),
    isLoading,
    isError,
    onExplore,
  };
}

/** Maps `/kpi_metrics/top_yutrepia_writers` rows (PascalCase keys) to TopYutrepiaWritersCard rows. */
function mapTopYutrepiaWritersApiRows(apiList) {
  if (!Array.isArray(apiList) || apiList.length === 0) return [];
  return apiList.map((raw, idx) => {
    const cur = raw?.Current_Month;
    const prior = raw?.Prior_Month;
    return {
      id: `tyw-${idx}`,
      name: withDoctorNamePrefix(raw?.Name ?? ""),
      institutionName: (raw?.InstitutionName ?? "").toString().trim(),
      npiSpecialty: (raw?.NPISpecialty ?? "").toString().trim(),
      repEmail: (raw?.Rep_Email ?? "").toString().trim(),
      latestMonth: (raw?.Latest_Month ?? "").toString().trim(),
      previousMonth: (raw?.Previous_Month ?? "").toString().trim(),
      currentMonth:
        cur === null ||
        cur === undefined ||
        cur === "" ||
        Number.isNaN(Number(cur))
          ? null
          : Number(cur),
      priorMonth:
        prior === null ||
        prior === undefined ||
        prior === "" ||
        Number.isNaN(Number(prior))
          ? null
          : Number(prior),
    };
  });
}

/** Maps `/kpi_metrics/hcp_hco_account_changes` rows to HcpHcoAccountChangesCard rows. */
function mapHcpHcoAccountChangesApiRows(apiList, idPrefix, selectedProduct) {
  if (!Array.isArray(apiList) || apiList.length === 0) return [];
  return apiList
    .filter((raw) =>
      accountChangeRowMatchesSelectedProduct(raw, selectedProduct),
    )
    .map((raw, idx) => ({
      id: `${idPrefix}-${idx}`,
      accountName: (raw?.Entity_Name ?? "").toString().trim(),
      recordStatus: (raw?.Record_Status ?? "").toString().trim(),
    }));
}

function deNovoRowMatchesSelectedProduct(
  row,
  selectedProduct,
  { leadershipSelection = null } = {},
) {
  if (isAllProductGroupSelection(selectedProduct)) return true;

  const key = (selectedProduct ?? "").toString().trim().toUpperCase();
  if (!key) return true;

  const rowPg = (row?.Standardized_ProductGroup ?? row?.ProductGroup ?? "")
    .toString()
    .trim()
    .toUpperCase();
  const rowGroup = (row?.Group ?? row?.group ?? "")
    .toString()
    .trim()
    .toUpperCase();

  // Leadership dropdown is CPS / ALD / ROCS-* → map to product + sales group.
  if (leadershipSelection) {
    const productKey = (leadershipSelection.product ?? "")
      .toString()
      .trim()
      .toUpperCase();
    const salesGroupKey = (leadershipSelection.salesGroup ?? "")
      .toString()
      .trim()
      .toUpperCase();

    if (productKey) {
      const pgMatch =
        rowPg === productKey ||
        (productKey === "TYVASO" && rowPg.startsWith("TYVASO"));
      if (!pgMatch) return false;
    }
    if (salesGroupKey && rowGroup !== salesGroupKey) return false;
    return true;
  }

  if (!rowPg) return false;
  if (rowPg === key) return true;
  if (key === "TYVASO" && rowPg.startsWith("TYVASO")) return true;
  return false;
}

/** Maps de novo API payload (grouped or `{ de_novo }`) to DeNovoCard rows. */
function mapDeNovoApiToCardItems(
  deNovoData,
  selectedProduct,
  { leadershipSelection = null } = {},
) {
  const grouped = deNovoData?.de_novo ?? deNovoData ?? {};
  if (!grouped || typeof grouped !== "object") return [];

  const flattened = [];
  Object.entries(grouped).forEach(([productGroup, rows]) => {
    if (!Array.isArray(rows)) return;

    rows.forEach((row, idx) => {
      if (
        !deNovoRowMatchesSelectedProduct(row, selectedProduct, {
          leadershipSelection,
        })
      ) {
        return;
      }

      const description = (row?.Description ?? "").toString().trim();
      flattened.push({
        id: `${productGroup}-${idx}-${row?.EventDate ?? idx}`,
        description,
        severity: mapColorCodingToSeverity(row?.Color_Coding),
        colorCoding: row?.Color_Coding ?? null,
        eventDate: row?.EventDate ?? null,
        productGroup: row?.Standardized_ProductGroup ?? productGroup,
        raw: row,
      });
    });
  });

  return flattened.sort((a, b) => {
    const da = a.eventDate ? new Date(a.eventDate).getTime() : 0;
    const db = b.eventDate ? new Date(b.eventDate).getTime() : 0;
    return da - db;
  });
}

/**
 * Assembles props for DashboardRenderer widgets. Kept pure (no hooks) for clarity and testability.
 */
export function buildMyPerformanceWidgetProps({
  roleKey,
  salesRepProductMetrics,
  productList,
  effectiveSelectedProduct,
  handleProductsChange,
  isDropdownDisabled,
  locationSearch,
  navigate,
  salesrepMetrics,
  rowsPR,
  rowsHCP,
  pendingReferralsTotal = 0,
  insightCounts,
  handleExplorePatientTrend,
  handleExploreUtEngagement,
  handleExploreCompetitorDynamics,
  handleExplorePhysicianBehavior,
  insightsLoading = false,
  patientTrendInsights = [],
  physicianBehaviorInsights = [],
  utEngagementInsights = [],
  competitorDynamicsInsights = [],
  salesRepHcpIncrease,
  salesRepHcpDecrease,
  handleHcpGrowerExploreForSalesRep,
  handleDeNovoExplore,
  rbdKpi,
  rbdNationalPerformanceRows = [],
  rbdRegionalPerformanceRows = [],
  territoryOnTrack,
  territoryNotOnTrack,
  overallOnTrackPercent,
  overallNotOnTrackPercent,
  handleTerritoryExploreClick,
  rbdHcpIncrease,
  rbdHcpDecrease,
  handleHcpGrowerExploreForRbd,
  leadershipProductMetrics,
  selectedMedicineLeadership,
  leadershipAlertsForUI,
  leadershipSelection,
  selectedSalesGroupLeadership,
  goToChatWithPrompt,
  leadershipRegionsForCard,
  leadershipHcpGrowers,
  handleHcpGrowerExploreForLeadership,
  noGoalsDataAvailable,
  goalsDataDisclaimerShow,
  rbdSummaryLoading = false,
  salesRepMyPerformanceLoading = false,
  salesRepNationalRegionalLoading = false,
  salesRepPendingReferralsLoading = false,
  salesRepReachFrequencyLoading = false,
  treatmentRateRows = [],
  treatmentRateLoading = false,
  treatmentRateError = false,
  salesRepTopYutrepiaWritersLoading = false,
  salesRepHcpHcoAccountChangesLoading = false,
  deNovoData = null,
  deNovoLoading = false,
  tyvasoDpiDosageDistributionData = null,
  tyvasoDpiDosageDistributionLoading = false,
  tyvasoDpiDosageDistributionError = false,
  tyvasoDpiDosageNotificationData = null,
  tyvasoDpiDosageNotificationLoading = false,
  tyvasoDpiDosageNotificationError = false,
  hcpGrowersLoading = false,
  reportSummaryData = null,
  reportSummaryLoading = false,
  reportSummaryError = false,
}) {
  // `locationSearch` is a getter from the controller (a plain string still works).
  const currentSearch = () =>
    typeof locationSearch === "function" ? locationSearch() : locationSearch;
  const navigateToChat = (prefillPrompt) => {
    const params = new URLSearchParams(currentSearch());
    params.set("tab", "repassist");
    navigate(
      { pathname: "/", search: params.toString() },
      { state: { prefillPrompt, prefillId: Date.now() } },
    );
  };

  const salesRepTrackingProps =
    roleKey === "SALESREP"
      ? {
          title: "My Performance",
          buttonText: "Explore My Performance",
          status: salesRepProductMetrics?.Status,
          goal: salesRepProductMetrics?.["Goal Starts"] ?? 0,
          progress:
            salesRepProductMetrics?.REFERRALS ??
            salesRepProductMetrics?.Referrals ??
            0,
          todaysBenchmark: salesRepProductMetrics?.Todays_start_benchmark ?? 0,
          estimatedHitGoal: salesRepProductMetrics?.Referals_to_hit_goal,
          onTrackStarts: () => {
            navigateToChat(
              "How am I tracking against my goals for this quarter?",
            );
          },
          products: productList,
          selectedProduct: effectiveSelectedProduct,
          onProductChange: handleProductsChange,
          disableProductSelect: isDropdownDisabled,
          noGoalsData: noGoalsDataAvailable,
          goalsDataDisclaimerShow,
          isLoading: salesRepMyPerformanceLoading,
        }
      : null;

  // RBD hero card ("My Region's Performance"), rendered by the same
  // MyPerformanceSummaryCard as the sales rep hero. The /rbd_kpi payload has no
  // status field, so the On Track / Not On Track badge is intentionally omitted
  // (status left unset); add it here once the backend exposes a region status.
  const rbdSummaryProps =
    roleKey === "RBD"
      ? {
          title: "My Region's Performance",
          buttonText: "Explore My Region's Performance",
          goal: round2(rbdKpi?.Goals_Referral),
          progress: round2(rbdKpi?.Progress_Referrals),
          todaysBenchmark: Math.round(rbdKpi?.Benchmark ?? 0),
          estimatedHitGoal: round2(rbdKpi?.REF_NEEDED_TO_MEET_GOAL),
          onTrackStarts: () => {
            navigateToChat("How is my region performing this quarter?");
          },
          noGoalsData: noGoalsDataAvailable,
          goalsDataDisclaimerShow,
          isLoading: rbdSummaryLoading,
        }
      : null;

  const buildSalesRepTerritoryRows = (list) =>
    (Array.isArray(list) ? list : []).map((item, idx) => ({
      id: `${item.TerritoryDisplayname || "territory"}-${idx + 1}`,
      name: item.TerritoryDisplayname || "Unknown",
      percent: Math.round(Number(item.PROG_TO_GOAL ?? 0) || 0),
      colorBanding: item.ColorBanding,
      highlight: String(item.TerritoryFlag || "").toLowerCase() === "yes",
    }));

  const salesRepNationalPerformanceRows =
    roleKey === "SALESREP"
      ? buildSalesRepTerritoryRows(
          getSalesRepPerformanceRowsByProduct(
            salesrepMetrics?.national_performance,
            effectiveSelectedProduct,
          ),
        )
      : [];

  const salesRepRegionalPerformanceRows =
    roleKey === "SALESREP"
      ? buildSalesRepTerritoryRows(
          getSalesRepPerformanceRowsByProduct(
            salesrepMetrics?.regional_performance,
            effectiveSelectedProduct,
          ),
        )
      : [];

  // Region is the asker's own and is identical across products, so derive it
  // from the full payload (SALESREP) or the already-sliced rows (RBD).
  const salesRepRegionSubtitle = buildRegionalPerformanceSubtitle(
    getRegionDisplayName(salesrepMetrics?.regional_performance),
  );
  const rbdRegionSubtitle = buildRegionalPerformanceSubtitle(
    getRegionDisplayName(rbdRegionalPerformanceRows),
  );

  return {
    ...(salesRepTrackingProps
      ? { salesRepPerformanceSummary: salesRepTrackingProps }
      : {}),
    ...(rbdSummaryProps ? { rbdPerformanceSummary: rbdSummaryProps } : {}),
    ...(roleKey === "SALESREP"
      ? {
          salesRepNationalPerformance: {
            rows: salesRepNationalPerformanceRows,
            isLoading: salesRepNationalRegionalLoading,
            onExplore: () =>
              navigateToChat(
                "How am I tracking against my goals for this quarter?",
              ),
          },
          salesRepRegionalPerformance: {
            rows: salesRepRegionalPerformanceRows,
            subtitle: salesRepRegionSubtitle,
            isLoading: salesRepNationalRegionalLoading,
            onExplore: () =>
              navigateToChat(
                "How am I tracking against my goals for this quarter?",
              ),
          },
          nationalPerformance: {
            rows: salesRepNationalPerformanceRows,
            isLoading: salesRepNationalRegionalLoading,
            onExplore: () =>
              navigateToChat(
                "How am I tracking against my goals for this quarter?",
              ),
          },
          regionalTerritoryPerformance: {
            rows: salesRepRegionalPerformanceRows,
            subtitle: salesRepRegionSubtitle,
            isLoading: salesRepNationalRegionalLoading,
            onExplore: () =>
              navigateToChat(
                "How am I tracking against my goals for this quarter?",
              ),
          },
          insightPatientTrends: {
            title: "Patient Trends",
            badgeColor: "#F1B260",
            rows: patientTrendInsights,
            isLoading: insightsLoading,
            onExplore: handleExplorePatientTrend,
            emptyMessage: "No patient trend insights found",
          },
          insightPhysicianBehavior: {
            title: "Physician Behavior",
            badgeColor: "#9B59B6",
            rows: physicianBehaviorInsights,
            isLoading: insightsLoading,
            onExplore: handleExplorePhysicianBehavior,
            emptyMessage: "No physician behavior insights found",
          },
          insightUtEngagement: {
            title: "UT Engagement",
            badgeColor: "#2CA66F",
            rows: utEngagementInsights,
            isLoading: insightsLoading,
            onExplore: handleExploreUtEngagement,
            emptyMessage: "No UT engagement insights found",
          },
          insightCompetitorDynamics: {
            title: "Competitor Dynamics",
            badgeColor: "#1F6AA5",
            rows: competitorDynamicsInsights,
            isLoading: insightsLoading,
            onExplore: handleExploreCompetitorDynamics,
            emptyMessage: "No competitor dynamics insights found",
          },
        }
      : {}),

    // RBD National / Regional Performance tiles (same tile components as the
    // sales rep performance tab), fed from /rbd_kpi national/regional arrays.
    ...(roleKey === "RBD"
      ? {
          nationalPerformance: {
            rows: buildSalesRepTerritoryRows(rbdNationalPerformanceRows),
            isLoading: rbdSummaryLoading,
            onExplore: () =>
              navigateToChat(
                "How am I tracking against my goals for this quarter?",
              ),
          },
          regionalTerritoryPerformance: {
            rows: buildSalesRepTerritoryRows(rbdRegionalPerformanceRows),
            subtitle: rbdRegionSubtitle,
            isLoading: rbdSummaryLoading,
            onExplore: () =>
              navigateToChat(
                "How am I tracking against my goals for this quarter?",
              ),
          },
        }
      : {}),

    startTracking:
      roleKey === "SALESREP"
        ? salesRepTrackingProps
        : roleKey === "RBD"
          ? {
              goal: round2(rbdKpi?.Goals_Referral),
              buttonText: "Explore Performance",
              progress: round2(rbdKpi?.Progress_Referrals),
              todaysBenchmark: Math.round(rbdKpi?.Benchmark ?? 0),
              estimatedHitGoal: round2(rbdKpi?.REF_NEEDED_TO_MEET_GOAL),
              metricLabel: "Referrals",
              progressTooltipText:
                "The total referrals count for your region in the current quarter.",
              onTrackStarts: () => {
                navigateToChat("How is my region performing this quarter?");
              },
              products: productList,
              selectedProduct: effectiveSelectedProduct,
              onProductChange: handleProductsChange,
              disableProductSelect: isDropdownDisabled,
              noGoalsData: noGoalsDataAvailable,
              goalsDataDisclaimerShow,
            }
          : {
              title: "National Referral Tracking by Product Group",
              goal: Math.round(leadershipProductMetrics?.goal ?? 0),
              progress: Math.round(leadershipProductMetrics?.referrals ?? 0),
              todaysBenchmark: Math.round(
                leadershipProductMetrics?.benchmark ?? 0,
              ),
              estimatedHitGoal: Math.round(
                leadershipProductMetrics?.referralRequiredToHitGoal ?? 0,
              ),
              metricLabel: "Referrals",
              progressTooltipText:
                "The total referrals count for your region in the current quarter.",
              onTrackStarts: () => {
                navigateToChat(
                  `Summarize the national performance of ${selectedMedicineLeadership}`,
                );
              },
              products: productList,
              selectedProduct: effectiveSelectedProduct,
              onProductChange: handleProductsChange,
              disableProductSelect: isDropdownDisabled,
              noGoalsData: noGoalsDataAvailable,
              goalsDataDisclaimerShow,
            },

    pendingReferrals: {
      title: "Summary of Pending Referrals",
      pendingReferrals: roleKey === "SALESREP" ? pendingReferralsTotal : 0,
      rowsPR,
      rowsHCP,
      isLoading:
        roleKey === "SALESREP" ? salesRepPendingReferralsLoading : false,
      onExplore: () =>
        navigateToChat(
          "Give me a summary of my pending referrals this quarter.",
        ),
    },
    keyInsights: {
      title: "Key Insights",
      patientTrendCount: insightCounts.patientTrend,
      utEngagementCount: insightCounts.utEngagement,
      competitorDynamicsCount: insightCounts.competitorDynamics,
      physicianBehaviourCount: insightCounts.physicianBehavior,
      onExplorePatientTrend: handleExplorePatientTrend,
      onExploreUtEngagement: handleExploreUtEngagement,
      onExploreCompetitorDynamics: handleExploreCompetitorDynamics,
      onExplorePhysicianBehavior: handleExplorePhysicianBehavior,
    },
    insightCategoryCards: {
      isLoading: insightsLoading,
      patientTrends: patientTrendInsights,
      physicianBehavior: physicianBehaviorInsights,
      utEngagement: utEngagementInsights,
      competitorDynamics: competitorDynamicsInsights,
      onExplorePatientTrend: handleExplorePatientTrend,
      onExplorePhysicianBehavior: handleExplorePhysicianBehavior,
      onExploreUtEngagement: handleExploreUtEngagement,
      onExploreCompetitorDynamics: handleExploreCompetitorDynamics,
    },
    reachAndFrequency:
      roleKey === "SALESREP"
        ? {
            rows: mapReachFrequencyApiRowsToCardRows(
              salesrepMetrics?.reach_frequency,
            ),
            isLoading: salesRepReachFrequencyLoading,
            onExplore: () => {
              navigateToChat(
                "What is my reach and frequency for my territory by decile?",
              );
            },
          }
        : {
            rows: [],
            isLoading: false,
          },

    treatmentRate:
      roleKey === "SALESREP" || roleKey === "RBD" || roleKey === "LEADERSHIP"
        ? buildTreatmentRateWidgetProps({
            treatmentRateRows,
            effectiveSelectedProduct,
            leadershipSelection:
              roleKey === "LEADERSHIP" ? leadershipSelection : null,
            isLeadership: roleKey === "LEADERSHIP",
            isLoading: treatmentRateLoading,
            isError: treatmentRateError,
            onExplore: () =>
              navigateToChat(
                "What are the trends in treatment rate and time to therapy for my territory?",
              ),
          })
        : {
            data: [],
            isLoading: false,
            isError: false,
          },

    topYutrepiaWriters:
      roleKey === "SALESREP"
        ? {
            rows: mapTopYutrepiaWritersApiRows(
              salesrepMetrics?.top_yutrepia_writers,
            ),
            isLoading: salesRepTopYutrepiaWritersLoading,
            onExplore: (row) => {
              const name = (row?.name ?? "").toString().trim() || "this HCP";
              navigateToChat(`Help me prep for a call with ${name}`);
            },
          }
        : {
            rows: [],
            isLoading: false,
          },

    hcpHcoAccountChanges:
      roleKey === "SALESREP"
        ? {
            hcpRows: mapHcpHcoAccountChangesApiRows(
              salesrepMetrics?.hcp_hco_account_changes?.hcp_rows,
              "hcp",
              effectiveSelectedProduct,
            ),
            hcoRows: mapHcpHcoAccountChangesApiRows(
              salesrepMetrics?.hcp_hco_account_changes?.hco_rows,
              "hco",
              effectiveSelectedProduct,
            ),
            isLoading: salesRepHcpHcoAccountChangesLoading,
            onExplore: (row, entityType) => {
              const name =
                (row?.accountName ?? "").toString().trim() ||
                (entityType === "hco" ? "this HCO" : "this HCP");
              if (entityType === "hco") {
                navigateToChat(`Help me prep for a visit to ${name}`);
                return;
              }
              navigateToChat(`Help me prep for a call with ${name}`);
            },
          }
        : {
            hcpRows: [],
            hcoRows: [],
            isLoading: false,
          },

    deNovo: {
      items: mapDeNovoApiToCardItems(deNovoData, effectiveSelectedProduct, {
        leadershipSelection:
          roleKey === "LEADERSHIP" ? leadershipSelection : null,
      }),
      isLoading: deNovoLoading,
      onExplore: handleDeNovoExplore,
    },

    tyvasoDpiDosageDistribution: {
      rows:
        roleKey === "SALESREP" || roleKey === "RBD"
          ? mapTyvasoDpiDosageDistribution(tyvasoDpiDosageDistributionData)
          : [],
      isLoading: tyvasoDpiDosageDistributionLoading,
      isError: tyvasoDpiDosageDistributionError,
      onExplore: () =>
        navigateToChat(
          "Which HCPs with more than 1 active patient have no patients on 80 mcg?",
        ),
    },

    tyvasoDpiDosageNotification: {
      rows:
        roleKey === "SALESREP" || roleKey === "RBD"
          ? mapTyvasoDpiDosageNotifications(tyvasoDpiDosageNotificationData)
          : [],
      isLoading: tyvasoDpiDosageNotificationLoading,
      isError: tyvasoDpiDosageNotificationError,
      onExplore: (row) => {
        const name = (row?.accountName ?? "").toString().trim() || "this HCP";
        navigateToChat(`Help me prep for a call with ${name}`);
      },
    },

    // 6X6 / 3X3 Report Summary — LEADERSHIP-scoped digest of the monthly
    // National Performance Excel ingest. The API returns a `pending_phase_2`
    // payload for RBD / SALESREP; passing the response through untouched
    // lets the widget render both `ok` and `pending_phase_2` states without
    // any dispatcher-side awareness of role.
    sixBySummary: {
      data: reportSummaryData,
      isLoading: reportSummaryLoading,
      isError: reportSummaryError,
      roleKey,
    },

    hcpGrowers:
      roleKey === "SALESREP"
        ? {
            increaseData: salesRepHcpIncrease,
            decreaseData: salesRepHcpDecrease,
            onExplore: handleHcpGrowerExploreForSalesRep,
            isLoading: hcpGrowersLoading,
          }
        : roleKey === "RBD"
          ? {
              increaseData: rbdHcpIncrease,
              decreaseData: rbdHcpDecrease,
              onExplore: handleHcpGrowerExploreForRbd,
              isLoading: hcpGrowersLoading,
            }
          : {
              increaseData: leadershipHcpGrowers.increase,
              decreaseData: leadershipHcpGrowers.decrease,
              onExplore: handleHcpGrowerExploreForLeadership,
              isLoading: hcpGrowersLoading,
            },

    regionalPerformance: {
      rank: Math.round(rbdKpi?.Rank ?? 0),
      regionPercent: round2(rbdKpi?.Region_Target_Reach_Perc),
      nationalAvgPercent: round2(rbdKpi?.National_Avg_Target_Reach_Perc),
      noGoalsDataAvailable,
      goalsDataDisclaimerShow,
    },
    territoryPerformance: {
      onTrack: territoryOnTrack,
      notOnTrack: territoryNotOnTrack,
      onTrackPercent: overallOnTrackPercent,
      notOnTrackPercent: overallNotOnTrackPercent,
      onExplore: handleTerritoryExploreClick,
      noGoalsDataAvailable,
      goalsDataDisclaimerShow,
    },

    kpiAlerts: {
      alerts: leadershipAlertsForUI,
      selectedProduct:
        effectiveSelectedProduct === "All"
          ? "All"
          : leadershipSelection?.product || null,
      selectedSalesGroup:
        effectiveSelectedProduct === "All"
          ? null
          : selectedSalesGroupLeadership,
      onExplore: (alert) => {
        const prompt = buildLeadershipAlertPrompt(alert);
        goToChatWithPrompt(prompt);
      },
      noGoalsDataAvailable,
    },
    regionTracking: {
      regions: leadershipRegionsForCard,
      onExplore: (region) => {
        const regionName = region?.name ?? "this region";
        const prompt = `Summarize the performance of ${selectedSalesGroupLeadership} in ${regionName} region this quarter?`;
        const params = new URLSearchParams(currentSearch());
        params.set("tab", "repassist");
        navigate(
          { pathname: "/", search: params.toString() },
          { state: { prefillPrompt: prompt, prefillId: Date.now() } },
        );
      },
      noGoalsDataAvailable,
      goalsDataDisclaimerShow,
    },
  };
}
